import type { FORM_DATA_KEYS_MAP } from '@/shared/constants/api.constants'
import type { Nullable } from './common.types'
export type SendFormDataKeys = keyof typeof FORM_DATA_KEYS_MAP
export type SendFormDataFields = {
  [key in SendFormDataKeys]: unknown
}

export interface SendFormDataResult {
  success: boolean
  errorMessage: string
}

export enum CountryBoundType {
  'OUTBUND' = 'outbound',
  'INBUND' = 'inbound',
}

export enum CountryBoundNumber {
  'OUTBUND' = 0,
  'INBUND' = 1,
}

export enum TravelType {
  'SINGLE' = '001',
  'MULTITRIP' = '002',
}

export interface Country {
  id: string
  name: string
  popular?: string // Api send number as string. Exampls: "popular": "1".
  isocode: string
  code: string
  searchString: string
}

export type PromocodeProductType = 'OD' | 'TP' | 'HOME' | 'TRAVEL' | 'TRAVEL_I' | 'PAB'
export type PromocodeTransactionType = 'POL' | 'REN'

export interface ApiPromocodeUsage {
  discountPerc: Nullable<number>
  expirationDate: Nullable<number>
  isAffinity: Nullable<number>
  productType: Nullable<PromocodeProductType>
  transactionType: Nullable<PromocodeTransactionType>
}

export interface FoundedCountry {
  code: string
  desc: string
  isocode: string
}

export interface UaeWording {
  categoryID: number
  category: string
  docFullName: string
  Link: string
  SortOrder: number
}

export interface UaeFAQ {
  Category: string
  Anchor: string
  CategoryShortName: string
  CategoryID: number
  Question: string
  Answer: string
  IsArabic: number // 0 or 1
  SortOrder: number
}

export enum WordingDocName {
  TERRORISM = 'terrorismext',
  POLICY_WORDING = 'travelouttc',
  POLICY_WORDING_INBOUND = 'tavelinbtc',
  WINTER_SPORTD = 'wsext',
  PRIVACY_POLICY = 'privpolicy',
  ROADSIDE_ASSISTANCE = 'roadassist',
}

export enum FileCategory {
  GENERAL = 1,
  TRAVEL = 7,
}

interface PlanBase {
  id: string
  name: string
  description: string
}

interface PlanBenefit extends PlanBase {
  price: string
}

export interface PlanIncluded extends PlanBase {
  value: number
  benefits: PlanBenefit[]
  subtitles?: string[]
}

interface PlanAddon extends PlanBase {
  checked: boolean
}

interface PlanExcessCover {
  code: string
  name: string
  premium: string //actually it's a number in string, example { premium: "50" },
  help: null
  wordings: null
  benefits: null
}

interface PlanDiscount {
  name: string
  premium: string
}

export enum SchemeType {
  WorldwideExcl = 'worldwideexcl',
  Worldwide = 'worldwide',
  Regional = 'regional',
  FlyEurope = 'flyeurope',
}

export interface Plan {
  name: string
  price: number
  deductible: number
  discount: number
  discounts: PlanDiscount[]
  schemecode: string
  schemeTypeCode: SchemeType
  productcode: string
  ids: PlanIncluded[]
  included: PlanIncluded[]
  addons: PlanAddon[]
  excessCovers: PlanExcessCover[]
  priceWithCoinsDiscount?: number | string
  coinsDiscountValue?: number
}

export interface GetTravelQuoteUaeResponse {
  success: boolean
  errorMessage: string
  numadults: number
  numchildren: number
  quoteno: number
  schemes: Plan[]
  addons: PlanBase[]
  productBonus: number
  coinsDiscountPrice?: number | string
  quoteWithCoinsDiscount?: number | string
}

export enum PromoCodeTypes {
  AFFINITY = 'affinity',
}

export interface Nationality {
  nationalityCode: string
  nationalityName: string
  searchString?: string
  isoCode?: string
}

export interface UaeCity {
  code: string
  desc: string
  value: null
  descAr: null
  longDesc: null
  longDescBl: null
  colourCode: null
}

export interface Relation {
  code: string
  desc: string
  value: string
  descAr: null
  longDesc: null
  longDescBl: null
  colourCode: null
}

export interface TravelerInformation {
  srNo?: number
  firstName: string
  lastName: string
  dateOfBirth: string
  relationCode: string
  relationName: string
  nationalityCode: string
  nationalityName: string
  passportNo: string
  winSports?: number | string
}
export interface VisitorInformation extends TravelerInformation {
  visaNo: string
  email: string
  phone: string
}
export interface GetTravelersResponse {
  list: TravelerInformation[]
}

export interface VisitorPolicy {
  name: string
  email: string
  policyNo: string
  link: string
  receiptlink: string
}

export interface GetPolicyResponse {
  visitorPolicies: VisitorPolicy[]
  policyNo: string
  link: string
  receiptlink: string
  showpromo: boolean
  cyberSecurityWordingsLink?: string
  combinedReceiptLink?: string
  isUserRegistered: boolean
  earnedBonuses: number | null
}

export interface GetAffinityData {
  affinityName: string
  promoCode: string
  logo: string
  motorDesc: string
  travelDesc: string
  homeDesc: string
  boatDesc: any
  pabDesc: string
  effFmDate: number
  effToDate: number
  codeUsage: ApiPromocodeUsage[]
  agentLogo: string
}

export interface PaymentOption {
  paymCond: string
  paymDesc: string
  paymtType: string
}

export interface GetAbDistributionResponse<T> {
  experiment: string
  group: T | null
}

export interface EnterpriseContact {
  ContactKey: string
  ContactType: string
  Product: string
  Region: string
  Value: string
}

interface CommonAddon {
  id: string
  isActive: boolean
}

export interface GetTravelPricePayload {
  quoteno: string
  schemecode: string
  productcode: string
  commonAddons: CommonAddon[]
  personalAddons: {
    srNo: number
    addons: CommonAddon[]
  }
  selectproduct: 0 | 1
  redirectPageCode?: string
}

export interface GetTravelPriceResponse {
  success: boolean
  errorMessage: string
  totalprice: number
  totalBonus: number
}
